/* Enhanced styling and structure applied to FundUpdate to match Portfolio aesthetics while preserving single-fund functionality */

import { Box } from '@mui/material';
import BackendService from 'BackendService';
import InvestorContext from 'InvestorContext';
import React, { Component } from 'react';
import FundUpdateAllocationSelection from '../components/fundUpdate/FundUpdateAllocationSelection';
import FundUpdateCashflowSection from '../components/fundUpdate/FundUpdateCashflowSection';
import FundUpdateMetricsRow from '../components/fundUpdate/FundUpdateMetricsRow';
import FundUpdatesReadView from '../components/FundUpdatesReadView';
import StyledSection from '../components/styles/StyledSection';
import { determineNextTone, StyledHeading } from '../components/styles/styles';
import { BreadcrumbsManager } from '../context/BreadcrumbsManager';
import { AdvisedFor } from '../models/Advisor';
import FundOpportunity from '../models/FundOpportunity';
import { InvestorAllocationsResponse } from '../models/InvestmentEntity';
import Investor from '../models/Investor';
import IRRDataPoint, { IRRData, IRROverallData } from '../models/IRRDataPoint';
import { IRRUpdate } from '../models/IRRUpdate';
import { fetchFundUpdateAllocations } from './fundUpdate/fetchFundUpdateAllocations';
import {
  getSelectedInvestmentAmount,
  normalizeIrrDataForInvestmentRatio,
  type FundUpdateChartType,
} from './fundUpdate/fundUpdateUtils';
import { Palette } from 'Palette';
import BaseBackendService from '../services/BaseBackendService';
import { investmentValidationService } from '../services/InvestmentValidationService';
import { validateInvestment } from '../services/InvestmentValidator';

export interface FundUpdateState {
  editMode?: boolean;
  irrUpdates: IRRUpdate[];
  chartData: IRRDataPoint[];
  overallData?: IRROverallData;
  selectedLabel: string;
  chartType: FundUpdateChartType;
  simulatedInvestment?: number;
  simulatedInvestmentError?: string;
  toolbarWidth: number;
  allocationsData?: InvestorAllocationsResponse & {
    entities?: Map<string, unknown>;
  };
  selectedEntityIds: Set<string>;
  includeUnallocated: boolean;
  allocationsLoading: boolean;
  allocationsError?: string;
  combinedEntityView: boolean;
  chartDataLoading: boolean;
}

export interface FundUpdateBaseProps {
  parentTone?: keyof typeof Palette;
  contentTone?: keyof typeof Palette;
  metricsTone?: keyof typeof Palette;
  fundOpportunity: FundOpportunity;
  investor?: Investor;
  advisor?: AdvisedFor;
  canEdit?: boolean;
  prependSection?: React.ReactNode;
  appendSection?: React.ReactNode;
  backendService?: BaseBackendService | (() => BaseBackendService);
  renderAdminEditPanel?: (props: {
    fundOpportunityId: string;
    fundName: string;
    onToolbarWidthChange: (width: number) => void;
  }) => React.ReactNode;
  /**
   * Cashflow Reports is admin-only and pulls in admin-only BackendService
   * methods — rendered via a callback (like renderAdminEditPanel) rather than
   * imported directly here, so this shared file never drags admin-only code
   * into the non-admin bundle/type-check.
   */
  renderCashflowReportsSection?: () => React.ReactNode;
}

export class FundUpdateBaseImpl extends Component<FundUpdateBaseProps, FundUpdateState> {
  private parsedDataCache: Map<string, IRRData> = new Map();
  static contextType = InvestorContext;
  context!: React.ContextType<typeof InvestorContext>;

  static defaultProps = {
    parentTone: 'default',
  };
  parentTone: string | number;
  contentTone: string | number;
  metricsTone: string | number;

  getBackendService(): BaseBackendService {
    if (this.props.backendService) {
      return typeof this.props.backendService === 'function'
        ? this.props.backendService()
        : this.props.backendService;
    }
    return new BackendService();
  }

  validateIndicativeInvestment(value?: string) {
    investmentValidationService
      .validateInvestment(value)
      .then((result) => {
        this.setState({
          simulatedInvestmentError: result.validationError,
          simulatedInvestment: result.validInvestment,
        });
      })
      .catch((error) => {
        console.error('Failed to validate investment:', error);
        const fallbackResult = validateInvestment(value);
        this.setState({
          simulatedInvestmentError: fallbackResult.validationError,
          simulatedInvestment: fallbackResult.validInvestment,
        });
      });
  }

  constructor(props: FundUpdateBaseProps) {
    super(props);

    const parentTone = props.parentTone ?? 'default';
    const contentTone = props.contentTone ?? determineNextTone(parentTone);
    const metricsTone = props.metricsTone ?? determineNextTone(contentTone);

    this.parentTone = parentTone;
    this.contentTone = contentTone;
    this.metricsTone = metricsTone;

    this.state = {
      irrUpdates: [],
      chartData: [],
      selectedLabel: '',
      chartType: 'Cumulative',
      toolbarWidth: props.canEdit ? 0 : 150,
      editMode: false,
      selectedEntityIds: new Set(),
      includeUnallocated: false,
      allocationsLoading: false,
      combinedEntityView: true,
      chartDataLoading: false,
    };
  }

  get summaryTitle() {
    if (this.props.fundOpportunity) {
      return this.props.fundOpportunity.summaryTitle;
    }

    return this.context?.investors?.find(
      (investor) => investor.fundOpportunityId === this.props.fundOpportunity.id,
    )?.fundOpportunity?.summaryTitle;
  }

  componentDidMount() {
    if (BreadcrumbsManager.peek()?.href?.match(/(opportunities|fundOpportunities)\/[^/]+/)) {
      BreadcrumbsManager.pop();
    }

    BreadcrumbsManager.push({
      label: `${this.props.fundOpportunity.summaryTitle || this.props.fundOpportunity.name} - Update`,
      href: `/portfolio/opportunities/${this.props.fundOpportunity.id}`,
    });

    this.loadIRRUpdates();

    if (this.props.investor) {
      this.fetchAllocations();
    }
  }

  // Exposed so a sibling with its own separate copy of the fund's IRR
  // updates (the embedded Cashflow Reports section) can tell this component
  // to refetch after an upload/delete — via a ref, not a remount, so it
  // doesn't reset that sibling's own local UI state (e.g. its expanded/
  // collapsed toggle).
  loadIRRUpdates() {
    this.getBackendService()
      .getLatestIRRUpdatesByLabel(this.props.fundOpportunity.id)
      .then((updates) => {
        const selectedLabel = updates[updates.length - 1]?.label;

        this.setState({ irrUpdates: updates, selectedLabel }, () => {
          if (selectedLabel) this.fetchParsedIRRData(selectedLabel);
        });
      });
  }

  fetchAllocations() {
    if (!this.props.investor) return;

    this.setState({ allocationsLoading: true, allocationsError: undefined });

    fetchFundUpdateAllocations(this.getBackendService() as never, this.props.investor).then(
      (result) => {
        if (result.ok) {
          this.setState({
            allocationsData: result.allocationsData,
            selectedEntityIds: result.selectedEntityIds,
            includeUnallocated: result.includeUnallocated,
            allocationsLoading: false,
          });
        } else {
          this.setState({
            allocationsError: result.error,
            allocationsLoading: false,
          });
        }
      },
    );
  }

  componentDidUpdate(_prevprops: FundUpdateBaseProps, prevState: FundUpdateState) {
    if (
      prevState.simulatedInvestment !== this.state.simulatedInvestment ||
      prevState.selectedEntityIds !== this.state.selectedEntityIds ||
      prevState.includeUnallocated !== this.state.includeUnallocated
    ) {
      this.fetchParsedIRRData(this.state.selectedLabel);
    }
  }

  getSelectedInvestmentAmount(): number {
    return getSelectedInvestmentAmount(
      this.props.investor,
      this.state.allocationsData,
      this.state.selectedEntityIds,
      this.state.simulatedInvestment,
    );
  }

  fetchParsedIRRData(label: string) {
    const irrUpdate = this.state.irrUpdates.find((u) => u.label === label);
    if (!irrUpdate) return;

    this.setState({ chartDataLoading: true });

    const fundSize = this.props.fundOpportunity.fundSize;
    const investment = this.getSelectedInvestmentAmount();
    const ratio = investment && fundSize ? investment / fundSize : 1;

    const startTime = Date.now();
    const minLoadingTime = 500;

    this.getBackendService()
      .getParsedIRRDataForLabel(this.props.fundOpportunity.id, irrUpdate.month)
      .then((rawData: IRRData) => {
        const scaledData = normalizeIrrDataForInvestmentRatio(rawData, ratio);
        this.parsedDataCache.set(label, scaledData);

        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        setTimeout(() => {
          this.setState({
            overallData: scaledData.overall,
            chartData: scaledData.historical,
            chartDataLoading: false,
          });
        }, remainingTime);
      })
      .catch((err) => {
        console.error('Failed to fetch IRR data from GCS:', err);

        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        setTimeout(() => {
          this.setState({ chartDataLoading: false });
        }, remainingTime);
      });
  }

  onToolbarWidthChange = (width: number) => {
    this.setState({ toolbarWidth: width });
  };

  handleEntitySelection = (entityId: string, selected: boolean) => {
    this.setState((prevState) => {
      const newSelectedEntityIds = new Set(prevState.selectedEntityIds);
      if (selected) {
        newSelectedEntityIds.add(entityId);
      } else {
        newSelectedEntityIds.delete(entityId);
      }
      return { selectedEntityIds: newSelectedEntityIds };
    });
  };

  handlePeriodChange = (label: string) => {
    this.setState({ selectedLabel: label });
    this.fetchParsedIRRData(label);
  };

  render() {
    const { prependSection, appendSection, canEdit, fundOpportunity, investor } = this.props;
    const investmentKey = this.getSelectedInvestmentAmount();

    const editPanelOrReadView = canEdit ? (
      (this.props.renderAdminEditPanel?.({
        fundOpportunityId: fundOpportunity.id,
        fundName: fundOpportunity.name || '',
        onToolbarWidthChange: this.onToolbarWidthChange,
      }) ?? null)
    ) : (
      <FundUpdatesReadView
        fundOpportunityId={fundOpportunity.id}
        onToolbarWidthChange={this.onToolbarWidthChange}
      />
    );

    const documentsAndSummarySection = (
      <>
        {prependSection}
        {!canEdit && (
          <StyledSection tone="default" noSidePaddingFrom="md" noTopPadding>
            <StyledHeading>
              Fund Summary{this.summaryTitle && ` – ${this.summaryTitle}`}
            </StyledHeading>
          </StyledSection>
        )}
        <StyledSection tone="tone1" sx={{ mb: canEdit ? 3 : undefined }}>
          <StyledHeading>{canEdit ? 'Financial Updates' : 'Fund Update'}</StyledHeading>

          {this.state.overallData && (
            <Box sx={{ maxWidth: canEdit ? '100%' : undefined, mt: canEdit ? 2 : undefined }}>
              <FundUpdateMetricsRow
                fundOpportunity={fundOpportunity}
                irrUpdates={this.state.irrUpdates}
                canEdit={canEdit}
              />
            </Box>
          )}

          <FundUpdateAllocationSelection
            investor={investor}
            allocationsData={this.state.allocationsData}
            selectedEntityIds={this.state.selectedEntityIds}
            allocationsLoading={this.state.allocationsLoading}
            allocationsError={this.state.allocationsError}
            onEntitySelection={this.handleEntitySelection}
          />

          <FundUpdateCashflowSection
            irrUpdates={this.state.irrUpdates}
            selectedLabel={this.state.selectedLabel}
            chartType={this.state.chartType}
            chartData={this.state.chartData}
            chartDataLoading={this.state.chartDataLoading}
            combinedEntityView={this.state.combinedEntityView}
            allocationsData={this.state.allocationsData}
            selectedEntityIds={this.state.selectedEntityIds}
            fundSize={fundOpportunity.fundSize ?? undefined}
            investmentKey={investmentKey}
            investor={investor}
            simulatedInvestment={this.state.simulatedInvestment}
            simulatedInvestmentError={this.state.simulatedInvestmentError}
            onPeriodChange={this.handlePeriodChange}
            onChartTypeChange={(chartType) => this.setState({ chartType })}
            onCombinedEntityViewChange={(combinedEntityView) =>
              this.setState({ combinedEntityView })
            }
            onSimulatedInvestmentChange={(value) => this.validateIndicativeInvestment(value)}
            canEdit={canEdit}
          />

          {canEdit && this.props.renderCashflowReportsSection?.()}
        </StyledSection>
      </>
    );

    return (
      <Box
        sx={{
          marginRight: canEdit ? `${this.state.toolbarWidth}px` : undefined,
          // Investor UI keeps its existing near-full-viewport width; admin now
          // relies on the standard MainLayoutBase content width instead, to
          // match the rest of the admin app's pages.
          maxWidth: canEdit ? undefined : { md: 'calc(100vw - (1rem * 2))' },
        }}
      >
        {canEdit ? (
          <>
            {editPanelOrReadView}
            {documentsAndSummarySection}
          </>
        ) : (
          <>
            {documentsAndSummarySection}
            {editPanelOrReadView}
          </>
        )}
        {appendSection}
      </Box>
    );
  }
}

const FundUpdateBase = FundUpdateBaseImpl;

export default FundUpdateBase;
